<template>
  <v-app>
    <!-- Names a test, pre-release or development deployment on every page (APP_BANNER). First on
         purpose: Vuetify stacks layout items in tree order, so it stays above the app bar. -->
    <lew-deployment-banner />
    <lew-error-notification />
    <lew-update-notification />
    <!-- Keyed with the create series' round, which changes only when "Speichern & neu" or "Speichern &
         kopieren" asks for the next form: the create view is then set up afresh, as a first visit sets
         it up (composables/useCreateSeries.ts). Constant otherwise, so every other page keeps its
         component exactly as before, and the route's props travel in the vnode. -->
    <router-view v-slot="{ Component }">
      <component :is="Component" :key="seriesRound" />
    </router-view>
    <!-- Outside the block below: Sentry's form also opens in the Erklärung's frame, and its own
         confirmation is hidden there too. -->
    <lew-feedback-sent-dialog />
    <!-- Mounted here, not in LewBaseLayout: every view wraps its own layout instance, so the layout is
         remounted on each navigation and could not host any of these. The onboarding walkthrough and
         the release dialog have to outlive a route change; the per-view explanation navigates between
         views on purpose. -->
    <!-- None belongs in the instance inside the Erklärung's own frame: the walkthrough would
         nest, and the onboarding tour would auto-start over the thing being explained. -->
    <template v-if="!isEmbed">
      <lew-onboarding-tour />
      <lew-view-help />
      <lew-release-notes-dialog />
      <!-- Asks before edits not saved yet are left behind; it has to outlive the page it asks about. -->
      <lew-unsaved-changes-dialog />
      <!-- Names the record a create series just made; it has to outlive the form it was made in. -->
      <lew-create-series-notice />
    </template>
  </v-app>
</template>

<script>
import LewErrorNotification from "@/components/general/LewNotification/LewErrorNotification"
import LewUpdateNotification from "@/components/general/LewNotification/LewUpdateNotification.vue"
import LewOnboardingTour from "@/components/general/LewOnboardingTour/LewOnboardingTour.vue"
import LewViewHelp from "@/components/general/LewViewHelp/LewViewHelp.vue"
import LewReleaseNotesDialog from "@/components/general/LewReleaseNotes/LewReleaseNotesDialog.vue"
import LewUnsavedChangesDialog from "@/components/general/LewUnsavedChanges/LewUnsavedChangesDialog.vue"
import LewFeedbackSentDialog from "@/components/general/LewFeedback/LewFeedbackSentDialog.vue"
import LewCreateSeriesNotice from "@/components/general/LewCreateSeries/LewCreateSeriesNotice.vue"
import LewDeploymentBanner from "@/components/layout/LewDeploymentBanner.vue"
import { useI18n } from "vue-i18n"
import { onMounted, onUnmounted } from "vue"
import { startVersionCheck, stopVersionCheck } from "@/services/versionCheck"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import { useDocumentTitleSync } from "@/composables/useDocumentTitle"
import { createSeriesRound } from "@/composables/useCreateSeries"

export default {
  components: {
    LewErrorNotification,
    LewUpdateNotification,
    LewOnboardingTour,
    LewViewHelp,
    LewReleaseNotesDialog,
    LewUnsavedChangesDialog,
    LewFeedbackSentDialog,
    LewDeploymentBanner,
    LewCreateSeriesNotice,
  },
  setup() {
    const { t, locale } = useI18n()

    // The tab title follows the current view — see composables/useDocumentTitle.ts.
    useDocumentTitleSync()

    // Started from the root component so the poll runs for signed-out visitors too — the login
    // page is exactly where a stale bundle is most likely to be sitting in a long-lived tab.
    // Not in the Erklärung's frame: it lives for as long as a dialog is open and has no tab to go
    // stale in, and an update notification inside the thing being explained is noise.
    if (!IS_VIEW_HELP_EMBED) {
      onMounted(startVersionCheck)
      onUnmounted(stopVersionCheck)
    }

    return { t, locale, isEmbed: IS_VIEW_HELP_EMBED, seriesRound: createSeriesRound }
  },
}
</script>

<style>
/* `--v-theme-background` is an RGB *triplet* ("244,245,247"), not a colour — so the old
   `background-color: var(--v-theme-background)` was an invalid declaration the browser dropped,
   and `#app` fell back to the page's white. Harmless while the app was light-only and white
   underneath; a white halo behind the dark theme otherwise. */
#app {
  background-color: rgb(var(--v-theme-background));
}
</style>
